import { useLocation } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Inbox, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useToast } from "../contexts/ToastContext";
import { notificationsService } from "../services/notifications";
import { AppNotification, NotificationKind, TaskSnooze } from "../types";
import "./Notifications.css";
import { relativeTime } from "../utils/relativeTime";

const KIND_LABELS: Record<NotificationKind, string> = {
  task_assigned: "Assigned",
  task_reassigned: "Reassigned",
  task_overdue: "Overdue",
  task_due_today: "Due today",
  task_due_soon: "Due soon",
  inspection_complete: "Inspection complete",
  task_comment: "Comment",
  high_risk_finding: "High-risk finding",
  task_status_changed: "Status change",
  lab_report_attention: "Lab alert",
  lab_incident: "Lab incident",
  lab_retest: "Re-test",
  wrmp_review_due: "Plan review",
  wrmp_acknowledged: "Plan signed",
};

const KIND_OPTIONS = Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }));

type SnoozeDays = 7 | 30 | 90;

const Notifications: React.FC = () => {
  usePageTitle('Notifications');
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [items, setItems] = useState<AppNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<"all" | NotificationKind>("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [snoozes, setSnoozes] = useState<TaskSnooze[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const reportError = useCallback(
    (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "An unexpected error occurred.";

      setError(message);
      showToast({
        type: "error",
        title: "Notifications error",
        message,
      });
    },
    [showToast]
  );

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const rows = await notificationsService.listSnoozes() || [];
      setSnoozes(rows);
      if (showSnoozed) {
        setSelected(new Set());
        return;
      }
      const response = await notificationsService.list({
        include_dismissed: showDismissed,
        unread_only: unreadOnly,
        kind: kindFilter === "all" ? undefined : kindFilter,
        limit: 100,
      });

      setItems(response.items);
      setTotal(response.total);
      setUnreadCount(response.unread_count);
      setSelected((current) => {
        const listedIds = new Set(response.items.map((item) => item.id));
        return new Set(Array.from(current).filter((id) => listedIds.has(id)));
      });
    } catch (err) {
      reportError(err);
    } finally {
      setLoading(false);
    }
  }, [kindFilter, reportError, showDismissed, showSnoozed, unreadOnly]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const selectedItems = useMemo(
    () => items.filter((item) => selected.has(item.id)),
    [items, selected]
  );

  const allSelected = items.length > 0 && selected.size === items.length;
  const partiallySelected = selected.size > 0 && !allSelected;

  const toggleSelected = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const toggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(items.map((item) => item.id))
    );
  };

  const markRead = async (ids: string[]) => {
    try {
      await notificationsService.markRead(ids);
      setSelected(new Set());
      await reload();
    } catch (err) {
      reportError(err);
    }
  };

  const showDismissedToast = (ids: string[]) => {
    showToast({
      type: "info",
      title: `Dismissed ${ids.length} notification(s)`,
      actions: [
        {
          label: "Undo",
          onClick: () => {
            void notificationsService
              .restore(ids)
              .then(reload)
              .catch(reportError);
          },
        },
      ],
    });
  };

  const dismiss = async (ids: string[]) => {
    try {
      await notificationsService.dismiss(ids);
      setSelected(new Set());
      showDismissedToast(ids);
      await reload();
    } catch (err) {
      reportError(err);
    }
  };

  // Gather before mutating so pagination offsets remain stable.
  const matchingNotifications = async (includeDismissed: boolean) => {
    const matching: AppNotification[] = [];
    let offset = 0;
    while (true) {
      const response = await notificationsService.list({
        kind: kindFilter === 'all' ? undefined : kindFilter,
        unread_only: unreadOnly, include_dismissed: includeDismissed, limit: 100, offset,
      });
      matching.push(...response.items);
      offset += response.items.length;
      if (!response.items.length || offset >= response.total) return matching;
    }
  };

  const handleMarkAllRead = async () => {
    try {
      if (kindFilter === 'all') await notificationsService.markAllRead();
      else {
        const matching = await matchingNotifications(showDismissed);
        const ids = Array.from(new Set(matching.filter(item => !item.read_at).map(item => item.id)));
        if (ids.length) await notificationsService.markRead(ids);
      }
      setSelected(new Set());
      await reload();
    } catch (err) {
      reportError(err);
    }
  };

  const handleDismissAll = async () => {
    try {
      const matching = await matchingNotifications(false);
      const ids = Array.from(new Set(matching.filter(item => !item.dismissed_at).map(item => item.id)));
      if (ids.length) await dismiss(ids);
    } catch (err) {
      reportError(err);
    }
  };

  const handleSnooze = async (days: SnoozeDays) => {
    const taskIds = Array.from(
      new Set(
        selectedItems
          .map((item) => item.task_id)
          .filter((id): id is string => Boolean(id))
      )
    );

    if (taskIds.length === 0) {
      showToast({
        type: "info",
        title: "Nothing to snooze",
        message: "The selected notifications aren't linked to tasks.",
      });
      return;
    }

    try {
      await notificationsService.snooze(taskIds, days);
      setSelected(new Set());
      showToast({
        type: "success",
        title: `Snoozed ${taskIds.length} task(s)`,
      });
      await reload();
    } catch (err) {
      reportError(err);
    }
  };

  const handleRowClick = (item: AppNotification) => {
    if (!item.read_at) {
      void notificationsService.markRead([item.id]).then(reload).catch(reportError);
    }

    if (item.link_path) {
      navigate(item.link_path);
    }
  };

  return (
    <div className="page-container notifications-page">
      <div className="page-header">
        <div>
          <h1>Notifications</h1>
          <p className="notif-subtitle">
            {unreadCount} unread{total > items.length ? ` · ${total} total` : ""}
          </p>
        </div>
      </div>


      <div className="toolbar notif-toolbar">
      <button className="notif-action-btn" aria-pressed={showSnoozed} onClick={() => { setShowSnoozed(value => !value); setSelected(new Set()); }}>Snoozed</button>
      {!showSnoozed && <>
        <label className="notif-select-all" title="Select all listed notifications">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(input) => {
              if (input) {
                input.indeterminate = partiallySelected;
              }
            }}
            onChange={toggleAll}
            disabled={items.length === 0}
            aria-label="Select all listed notifications"
          />
          <span>Select all</span>
        </label>

        <select
          className="filter-select"
          value={kindFilter}
          onChange={(event) =>
            setKindFilter(event.target.value as "all" | NotificationKind)
          }
          aria-label="Filter by notification kind"
        >
          <option value="all">All kinds</option>
          {KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className={`filter-toggle-btn${unreadOnly ? " active" : ""}`}
          onClick={() => setUnreadOnly((current) => !current)}
          aria-pressed={unreadOnly}
        >
          Unread only
        </button>

        <button
          type="button"
          className={`filter-toggle-btn${showDismissed ? " active" : ""}`}
          onClick={() => setShowDismissed((current) => !current)}
          aria-pressed={showDismissed}
        >
          Show dismissed
        </button>

        <div className="notif-toolbar-spacer" />

        <button
          type="button"
          className="notif-action-btn"
          onClick={() => void handleMarkAllRead()}
          disabled={unreadCount === 0}
        >
          Mark all read
        </button>
        <button
          type="button"
          className="notif-action-btn notif-action-danger"
          onClick={() => void handleDismissAll()}
          disabled={!items.some((item) => !item.dismissed_at)}
        >
          Dismiss all
        </button>
      </>}
      </div>

      {showSnoozed && !loading && <div className="notif-list">
        {!snoozes.length && <p>No active snoozes.</p>}
        {snoozes.map(snooze => <div className="notif-row" key={snooze.id}>
          <span><a href={`${location.pathname.startsWith('/portal') ? '/portal' : ''}/actions?task=${snooze.task_id}`}>{snooze.task_title || 'Action unavailable'}</a><small> Snoozed until {new Date(snooze.snoozed_until).toLocaleDateString('en-AU')}</small></span>
          <button className="btn btn-secondary btn-sm" onClick={() => { void notificationsService.unsnooze(snooze.task_id).then(reload).catch(reportError); }}>Unsnooze</button>
        </div>)}
      </div>}

      {selected.size > 0 && (
        <div className="notif-bulk-bar">
          <strong>{selected.size} selected</strong>
          <button
            type="button"
            className="notif-action-btn"
            onClick={() => void markRead(Array.from(selected))}
          >
            Mark read
          </button>
          <button
            type="button"
            className="notif-action-btn"
            onClick={() => void dismiss(Array.from(selected))}
          >
            Dismiss
          </button>
          <select
            className="filter-select"
            defaultValue=""
            onChange={(event) => {
              const days = Number(event.target.value);
              if (days === 7 || days === 30 || days === 90) {
                void handleSnooze(days);
              }
              event.target.value = "";
            }}
            aria-label="Snooze selected notification tasks"
          >
            <option value="" disabled>
              Snooze…
            </option>
            <option value="7">7 days</option>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
          </select>
        </div>
      )}

      {loading && <div className="notif-loading">Loading notifications...</div>}

      {!showSnoozed && !loading && items.length === 0 && (
        <div className="notif-empty">
          <Inbox size={48} aria-hidden="true" />
          <h2>{kindFilter !== "all" || unreadOnly || showDismissed ? "No notifications match these filters" : "You’re all caught up"}</h2>
        </div>
      )}

      {!showSnoozed && !loading && items.length > 0 && (
        <div className="notif-list">
          {items.map((item) => (
            <div
              key={item.id}
              className={[
                "notif-row",
                !item.read_at ? "notif-row-unread" : "",
                item.dismissed_at ? "notif-row-dismissed" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => handleRowClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleRowClick(item);
                }
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(item.id)}
                onClick={(event) => event.stopPropagation()}
                onChange={() => toggleSelected(item.id)}
                aria-label={`Select ${item.title}`}
              />

              <span
                className={`notif-severity notif-severity-${item.severity}`}
                aria-hidden="true"
              />

              <span className="notif-kind-badge">
                {KIND_LABELS[item.kind] || "Notification"}
              </span>

              <div className="notif-content">
                <strong className="notif-title">{item.title}</strong>
                {item.body && <p className="notif-body">{item.body}</p>}
                {snoozes.find(snooze => snooze.task_id === item.task_id) && <small>Snoozed until {new Date(snoozes.find(snooze => snooze.task_id === item.task_id)!.snoozed_until).toLocaleDateString('en-AU')}</small>}
                <span className="notif-time">
                  {relativeTime(item.created_at)}
                </span>
              </div>

              <button
                type="button"
                className="notif-dismiss-btn"
                title="Dismiss notification"
                aria-label={`Dismiss ${item.title}`}
                disabled={Boolean(item.dismissed_at)}
                onClick={(event) => {
                  event.stopPropagation();
                  void dismiss([item.id]);
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && error && <p className="notif-error">{error}</p>}
    </div>
  );
};

export default Notifications;
